import React, {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import ComponentConfig, {
  ComponentConfigProps,
  SaveButton,
  RemoveButton,
  Footer,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {EmojiListProps} from 'Event/template/Lefty/Dashboard/Sidebar/SidebarItem/EmojiList'
import {
  useAutoUpdateSidebarItem,
  useEditSidebarItem,
} from 'Event/template/Lefty/Dashboard/Sidebar/SidebarItem'
import Settings from 'Event/template/Lefty/Dashboard/Sidebar/SidebarItem/EmojiList/EmojiListConfig/Settings'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import RulesPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/RulesPanel'
import ScheduleInput from 'organization/Event/DashboardConfig/ComponentConfigPanel/ScheduleInput'
import {Rule} from 'Event/attendee-rules'
import {
  Emoji,
  useDeleteEmojiFile,
} from 'Event/Dashboard/components/EmojiList/emoji'

export function EmojiListConfig(
  props: ComponentConfigProps & {
    list: EmojiListProps
  },
) {
  const {showing, onClose, list} = props
  const [emojiWidth, setEmojiWidth] = useState<number | null | undefined>(
    list.emojiWidth,
  )
  const [emojis, setEmojis] = useState<Emoji['name'][]>(list.emojis)
  const [customEmojis, setCustomEmojis] = useState<Emoji['name'][]>(
    list.customEmojis ?? [],
  )
  const {removeList, update: updateItem} = useEditSidebarItem()
  const {control, setValue} = useForm()
  const [rules, setRules] = useState<Rule[]>(list.rules || [])
  const deleteFile = useDeleteEmojiFile()

  useEffect(() => {
    if (showing) {
      return
    }

    setEmojiWidth(list.emojiWidth)
    setEmojis(list.emojis)
    setCustomEmojis(list.customEmojis ?? [])
  }, [showing, list])

  const save = () => {
    const data = {
      emojiWidth,
      emojis,
      customEmojis,
      rules,
    }

    updateItem(data)
    onClose()
  }

  const addCustomEmoji = (emojiName: Emoji['name']) => {
    const changedCustomEmojies = [...customEmojis, emojiName]
    setCustomEmojis(changedCustomEmojies)
    setEmojis((curr) => [...curr, emojiName])

    const data: EmojiListProps = {
      ...list,
      customEmojis: changedCustomEmojies,
    }

    updateItem(data)
  }

  const removeCustomEmoji = (emojiName: Emoji['name']) => {
    // set current custom emojies
    const changedCustomEmojies = customEmojis.filter((e) => e !== emojiName)
    setCustomEmojis(changedCustomEmojies)

    // save custom emojies to API
    const data: EmojiListProps = {
      ...list,
      customEmojis: changedCustomEmojies,
    }
    updateItem(data)

    // Delete Custom emoji file
    deleteFile(emojiName)

    // Remove all emojies with that custom emoji
    const removed = emojis.filter((e) => e !== emojiName)
    setEmojis(removed)
  }

  useAutoUpdateSidebarItem({
    item: {
      emojiWidth,
      emojis,
      customEmojis,
    },
    when: showing,
  })

  return (
    <ComponentConfig title="Emojis" showing={showing} onClose={onClose}>
      <SettingsPanel>
        <Settings
          emojiWidth={emojiWidth}
          setEmojiWidth={setEmojiWidth}
          emojis={emojis}
          setEmojis={setEmojis}
          customEmojis={customEmojis}
          addCustomEmoji={addCustomEmoji}
          removeCustomEmoji={removeCustomEmoji}
        />
      </SettingsPanel>
      <RulesPanel rules={rules} setRules={setRules}>
        <ScheduleInput setValue={setValue} control={control} values={list} />
      </RulesPanel>
      <Footer>
        <SaveButton onClick={save} />
        <RemoveButton onClick={() => removeList([])} />
      </Footer>
    </ComponentConfig>
  )
}
